"use client"

import React, { useState } from "react"

import { useScrollIntoView } from "@mantine/hooks"
import { useCompletion } from "ai/react"
import { RepeatIcon } from "lucide-react"
import { useTranslations } from "next-intl"
import posthog from "posthog-js"
import { useFormContext } from "react-hook-form"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { Step, StepActions, useStep } from "@/components/ui/stepper"
import { BaseGeneratorComponent } from "@/components/generators/basegenerator/BaseGeneratorComponent"
import { BaseGeneratorForm } from "@/components/generators/basegenerator/BaseGeneratorForm"
import { BaseGeneratorHeader } from "@/components/generators/basegenerator/BaseGeneratorHeader"
import FantasyNameGeneratorFields from "@/components/generators/fantasynamen/FantasyNameGeneratorFields"
import { type FormValuesFantasy } from "@/components/generators/fantasynamen/FantasyNameGeneratorSchema"
import { type DnDSubcategory } from "@/components/generators/types"
import AllGeneratorResults from "@/components/generators/videogamesnamen/VideoGameNameGeneratorResults"
import { useAdditionalTranslations } from "@/components/providers"
import { useRouter } from "@/i18n/routing"

export type DnDGeneratorProps = {
  subject: string
  category: "dnd"
  subcategory: DnDSubcategory
}

export default function DnDNameGeneratorComponent({
  subject = "names",
}: DnDGeneratorProps) {
  const [generatedNames, setGeneratedNames] = useState<string[]>([])
  const form = useFormContext<FormValuesFantasy>()
  const { currentStep, setCurrentStep } = useStep()
  const t = useTranslations("Generator")
  const { translatedSubject } = useAdditionalTranslations()
  const router = useRouter()

  // Clear generated names when user goes back to the info step
  React.useEffect(() => {
    if (currentStep === "info") {
      setGeneratedNames([])
    }
  }, [currentStep])

  // Translations can only be requested outside of the useCompletion hook
  const toasterLimitReached = t("Shared.rate-limit-exceeded") + "!"
  const toasterBuyTokens = t("Shared.buy-tokens")
  const taosterBuyTokensDescription = t(
    "Shared.you-can-get-unlimited-tokens-for-5"
  )

  const { completion, complete, isLoading } = useCompletion({
    api: "/api/chat-llm",
    onResponse: (res) => {
      if (res.status === 429) {
        toast.error(toasterLimitReached, {
          description: taosterBuyTokensDescription,
          action: {
            label: toasterBuyTokens,
            onClick: () => {
              posthog.capture("clicked_buy_tokens", {
                generatorType: "dnd",
              })
              router.push("/rate-limit")
            },
          },
        })
        return
      }
      if (!res.ok) {
        toast.error("Failed to generate names")
        return
      }
      setCurrentStep("success")
      scrollIntoView()
    },
    onFinish: (prompt, completion) => {
      const newNames = completion.split(",").map((name) => name.trim())
      setGeneratedNames((prev) => [...prev, ...newNames])
    },
    onError: (error) => {
      console.error(error.message || "Something went wrong")
    },
  })

  const { scrollIntoView, targetRef } = useScrollIntoView<HTMLFormElement>({
    offset: 80,
  })

  const createPrompt = (data: FormValuesFantasy) => {
    const { buchstabe, sprache, klang, geschlecht, nameszusatz } = data
    const previousNames =
      generatedNames.length > 0
        ? `Previous suggestions (avoid these): ${generatedNames.join(", ")}`
        : ""

    const useCyrillic =
      sprache === "russian" ? "Use the cyrillic alphabet. " : ""

    return `Generate 10 unique names for a creature from the Dungeons and Dragons univierse.
      The subject is a ${subject.toUpperCase()}
      ${buchstabe === "random" ? "" : "The initial letter must be " + buchstabe + "."}
      The language of the names must be "${sprache}" and "${geschlecht}".
      ${useCyrillic}
      It must be "${klang}" sounding names.
      Every name should consist of ${nameszusatz === "name" ? "just a single name" : nameszusatz}
      ${previousNames}
      Respond with string of names seperated by a comma e.g. "name1,name2,name3" ...
      ONLY respond with a comma seperated string!
      Generate completely different names from any previous suggestions.`
  }

  const handleGeneration = async (data: FormValuesFantasy) => {
    const prompt = createPrompt(data)
    await complete(prompt)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleGeneration)} ref={targetRef}>
        <BaseGeneratorComponent>
          <Step value="info" className="space-y-14">
            <BaseGeneratorHeader
              imageLink={`/images/dnd/${subject}.webp`}
              description={t("Shared.name-generator-description")}
              subject={`${translatedSubject} ${t("FantasyNameGenerator.namegenerator")}`}
            />
            <BaseGeneratorForm
              buttonText={t("FantasyNameGenerator.start-btn", {
                0: translatedSubject,
              })}
              generatorType="dnd"
              isLoading={isLoading}
            >
              {/* DnDNameGenerator uses the Fields from the FantasyNameGeneratorFields Component */}
              <FantasyNameGeneratorFields subject={subject} />
            </BaseGeneratorForm>
          </Step>

          {/* --------  STEP 2 ---------- */}
          <Step value="success">
            <AllGeneratorResults
              isLoading={isLoading}
              completion={completion}
            />
            <StepActions className="mt-8 justify-center">
              <div className="flex flex-col items-center gap-2">
                <Button
                  type="submit"
                  name="again"
                  variant="default"
                  size="icon"
                  className="flex size-12 rounded-full"
                  disabled={form.formState.isSubmitting}
                >
                  <RepeatIcon className="size-6" />
                </Button>
                <span className="text-xs text-muted-foreground">
                  {t("Shared.try-again")}
                </span>
              </div>
            </StepActions>
          </Step>
        </BaseGeneratorComponent>
      </form>
    </Form>
  )
}
